import { makeStyles, tokens } from "@coreai/fluentui-react";

/** Shared sizing constants for consistent panel/card heights */
export const DOCK_CARD_MIN_HEIGHT = "72px";
export const DOCK_PANEL_CONTENT_MIN_HEIGHT = "88px";

/** Shadow-as-border: 1px ring via shadow instead of border, adapts via Fluent tokens */
const SHADOW_RING = `0 0 0 1px ${tokens.colorNeutralStroke1}`;

/** Shared pulse keyframes for skeleton loading indicators */
const SKELETON_PULSE = {
  animationName: {
    "0%": { opacity: 0.3 },
    "50%": { opacity: 0.6 },
    "100%": { opacity: 0.3 },
  },
  animationDuration: "1.5s",
  animationIterationCount: "infinite",
  animationTimingFunction: tokens.curveEasyEase,
  "@media (prefers-reduced-motion: reduce)": {
    animationName: "none",
    opacity: 0.4,
  },
} as const;

export const useOperationPanelStyles = makeStyles({
  // Expandable panel above the island
  panel: {
    pointerEvents: "auto",
    display: "grid",
    gridTemplateRows: "1fr",
    // Position above the dock bar without affecting wrapper height
    position: "absolute",
    bottom: `calc(100%)`,
    left: 0,
    right: 0,
    marginInline: "auto",
    marginBottom: tokens.spacingVerticalS,
    width: "100%",
    maxWidth: "600px",
    backgroundColor: `color-mix(in srgb, ${tokens.colorNeutralBackground1} 92%, transparent)`,
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    borderRadius: tokens.borderRadiusXLarge,
    boxShadow: tokens.shadow16,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    overflow: "hidden",
    transitionProperty: "grid-template-rows, opacity",
    transitionDuration: `${tokens.durationNormal}, ${tokens.durationFast}`,
    transitionTimingFunction: `${tokens.curveEasyEase}, ${tokens.curveEasyEase}`,
    "@media (prefers-reduced-motion: reduce)": {
      transition: "none",
    },
    opacity: 1,
  },
  panelCollapsed: {
    gridTemplateRows: "0fr",
    opacity: 0,
    pointerEvents: "none",
  },
  panelInner: {
    overflow: "hidden",
    minHeight: 0,
  },

  // Panel header with search
  panelHeader: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  searchInput: {
    flex: 1,
    backgroundColor: "transparent",
  },
  agentTabHeading: {
    flex: 1,
    fontSize: tokens.fontSizeBase400,
    color: tokens.colorNeutralForeground1,
    margin: 0,
  },
  closeButton: {
    minWidth: "auto",
    padding: tokens.spacingHorizontalXS,
  },
  shortcutHint: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalXS}`,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusSmall,
  },

  // Tab navigation
  tabList: {
    padding: `0 ${tokens.spacingHorizontalM}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    justifyContent: "space-between",
  },

  // Panel content area
  panelContent: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))",
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingHorizontalM} ${tokens.spacingHorizontalM} 0`,
    maxHeight: "250px",
    minHeight: DOCK_PANEL_CONTENT_MIN_HEIGHT,
    overflowY: "auto",
  },

  // Agent-tab content — full-width container that breaks out of the grid so
  // AgentTypeView's stacked horizontal cards fill the panel width.
  agentTabContent: {
    gridColumn: "1 / -1",
  },

  // Description bar
  descriptionBar: {
    minHeight: "28px",
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalM} 0`,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    borderTop: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center" as const,
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    transitionProperty: "opacity, transform",
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveDecelerateMax,
    "@media (prefers-reduced-motion: reduce)": {
      transition: "none",
    },
  },
  descriptionBarText: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
    color: tokens.colorNeutralForeground3,
  },

  // Operation card (draggable)
  operationCard: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "transparent",
    border: `1px solid transparent`,
    cursor: "grab",
    transitionProperty: "background-color, box-shadow, transform, opacity",
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveDecelerateMax,
    "@media (prefers-reduced-motion: reduce)": {
      transition: "none",
    },
    willChange: "transform",
    minHeight: DOCK_CARD_MIN_HEIGHT,
    "@media (hover: hover) and (pointer: fine)": {
      ":hover": {
        backgroundColor: tokens.colorNeutralBackground1Hover,
        border: "1px solid transparent",
        boxShadow: `${SHADOW_RING}, ${tokens.shadow4}`,
        transform: "translateY(-1px)",
      },
    },
    ":active": {
      cursor: "grabbing",
      transform: "scale(0.98)",
    },
    ":focus-visible": {
      outline: "none",
      boxShadow: `inset 0 0 0 2px ${tokens.colorBrandStroke1}, ${SHADOW_RING}, ${tokens.shadow4}`,
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  operationIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "36px",
    height: "36px",
    borderRadius: tokens.borderRadiusLarge,
    fontSize: "18px",
    color: tokens.colorNeutralForegroundOnBrand,
  },
  operationName: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
    textAlign: "center",
    lineHeight: tokens.lineHeightBase200,
  },

  // Browse button (for Actions, Triggers - opens full picker)
  browseCard: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    border: `1px dashed ${tokens.colorNeutralStroke1}`,
    cursor: "pointer",
    transitionProperty: "background-color, border-color, opacity, transform",
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveDecelerateMax,
    "@media (prefers-reduced-motion: reduce)": {
      transition: "none",
    },
    minHeight: DOCK_CARD_MIN_HEIGHT,
    "@media (hover: hover) and (pointer: fine)": {
      ":hover": {
        backgroundColor: tokens.colorNeutralBackground1Hover,
        border: `1px solid ${tokens.colorBrandStroke1}`,
      },
    },
    ":focus-visible": {
      outline: "none",
      boxShadow: `inset 0 0 0 2px ${tokens.colorBrandStroke1}`,
    },
  },
  browseIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "36px",
    height: "36px",
    borderRadius: tokens.borderRadiusMedium,
    fontSize: "18px",
  },
  browseText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    textAlign: "center",
    lineHeight: tokens.lineHeightBase200,
  },

  // Template grid (inline in Templates tab)
  templateGrid: {
    gridColumn: "1 / -1",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: tokens.spacingHorizontalS,
    width: "100%",
  },
  browseAllLink: {
    gridColumn: "1 / -1",
    display: "flex",
    justifyContent: "flex-end",
    padding: `${tokens.spacingVerticalXS} 0`,
    background: "none",
    border: "none",
    cursor: "pointer",
    "@media (hover: hover) and (pointer: fine)": {
      ':hover': {
        textDecorationLine: "underline",
      },
    },
  },

  // Empty state
  emptyState: {
    gridColumn: "1 / -1",
    padding: tokens.spacingVerticalL,
    textAlign: "center",
    color: tokens.colorNeutralForeground3,
  },

  // Screen reader only (visually hidden but accessible)
  srOnly: {
    position: "absolute",
    width: "1px",
    height: "1px",
    padding: 0,
    margin: "-1px",
    overflow: "hidden",
    clip: "rect(0, 0, 0, 0)",
    whiteSpace: "nowrap",
    border: "none",
  },

  // Skeleton loading card (matches operationCard dimensions)
  skeletonCard: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    minHeight: DOCK_CARD_MIN_HEIGHT,
  },
  skeletonIcon: {
    width: "36px",
    height: "36px",
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground3,
    ...SKELETON_PULSE,
  },
  skeletonText: {
    width: "48px",
    height: "12px",
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralBackground3,
    ...SKELETON_PULSE,
    animationDelay: tokens.durationFast,
  },

  // Connector icon image inside operation card
  connectorIconImg: {
    width: "20px",
    height: "20px",
    borderRadius: tokens.borderRadiusSmall,
    objectFit: "contain" as const,
  },
});
